"use client"

import { useConsentManager } from "@c15t/nextjs"
import type { PostHogConfig } from "posthog-js"
import posthog from "posthog-js"
import { useEffect } from "react"

export interface PostHogGateConfig {
  token: string
  options: Partial<PostHogConfig>
}

/**
 * React hook: initialises PostHog when the user grants `measurement` consent,
 * and opts the user out (clearing stored PostHog state) if they later revoke
 * it.
 *
 * Must be called inside <ConsentProvider>. The `options` reference must be
 * stable across renders (wrap with `useMemo` in the caller) to avoid
 * re-running the effect on every render.
 */
export function usePostHogConsentGate({ token, options }: PostHogGateConfig): void {
  const manager = useConsentManager()
  const measurementAllowed = manager.has("measurement")

  useEffect(() => {
    if (globalThis.window === undefined) return

    if (measurementAllowed) {
      if (!posthog.__loaded) {
        posthog.init(token, {
          // Default persistence; caller can override via `options`.
          persistence: "localStorage+cookie",
          ...options,
          person_profiles: "identified_only" as const,
        })
      }
      posthog.opt_in_capturing()
    } else {
      // Always opt out when consent is not granted - even before init - so any
      // pre-consent `posthog.capture(...)` call becomes a no-op instead of
      // being queued and flushed later.
      posthog.opt_out_capturing()
      if (posthog.__loaded) {
        // Revoke: clear previously persisted identifiers / events,
        // per GDPR Art 17 (right to erasure) and Art 7(3) (withdrawal).
        posthog.reset(true)
      }
    }
  }, [measurementAllowed, token, options])
}
